import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { MdAddCircle, MdEdit, MdDeleteOutline, MdFileDownload, MdKeyboardArrowDown, MdSearch, MdRefresh } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Utils, fetchWithAuth, can, formatDateTime, exportListCsv, getDefaultPageSize } from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

/**
 * Messages > Push > Templates - list page for saved Push Notification templates. Matches the
 * standard list-page pattern used elsewhere (e.g. WhatsApp's "Template Records" page, Support
 * Groups): filter accordion, row checkboxes + "Delete selected", Refresh/Export/Add Template,
 * a per-row action dropdown, and server-side pagination.
 * "New Template" / row "Edit" open the compose page (PushNotification.js) in template-editor
 * mode (?mode=template[&template_id=X]) - that same form doubles as the template editor.
 */
const PushTemplatesList = () => {
    const { appId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const pageSize = getDefaultPageSize();

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({ search: "" });
    const [draftFilters, setDraftFilters] = useState({ search: "" });
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);

    const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
        tableScrollRef,
        optionCount: 2,
        menuWidth: 180,
        useAnchored: true,
    });

    const loadData = async (nextPage = page, nextFilters = filters) => {
        if (!appId) return;
        setLoading(true);
        try {
            const res = await fetchWithAuth(Utils.baseurl + "application/push/templates/list", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    app_id: appId,
                    page: nextPage,
                    pageSize,
                    search: (nextFilters.search || "").trim() || undefined,
                }),
            });
            const result = await res.json();
            setRows(result.status ? (result.data || []) : []);
            setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
            setSelectedIds([]);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!appId) return;
        const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
        const search = searchParams.get("search") || "";
        const f = { search };
        setPage(p);
        setFilters(f);
        setDraftFilters(f);
        setFilterAccordionOpen(Boolean(search));
        loadData(p, f);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, searchParams]);

    const writeParams = (p, f) => {
        const sp = new URLSearchParams();
        if (p > 1) sp.set("page", String(p));
        if ((f.search || "").trim()) sp.set("search", f.search.trim());
        setSearchParams(sp, { replace: true });
    };

    const onApplyFilters = () => {
        const f = { ...draftFilters };
        setFilters(f);
        setFilterAccordionOpen(Boolean((f.search || "").trim()));
        writeParams(1, f);
    };

    const onResetFilters = () => {
        const f = { search: "" };
        setDraftFilters(f);
        setFilters(f);
        setFilterAccordionOpen(false);
        writeParams(1, f);
    };

    const toggleSelected = (id) => {
        const key = String(id);
        setSelectedIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
    };

    const toggleSelectAll = () => {
        const allIds = rows.map((r) => String(r.id));
        setSelectedIds((prev) => (allIds.every((id) => prev.includes(id)) ? [] : allIds));
    };

    const handleDelete = async (row) => {
        const confirmed = await Swal.fire({
            title: `Delete "${row.name}"?`,
            text: "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
        });
        if (!confirmed.isConfirmed) return;
        try {
            const res = await fetchWithAuth(Utils.baseurl + "application/push/templates/delete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ app_id: appId, id: row.id }),
            });
            const result = await res.json();
            if (result.status) {
                toast.success("Template deleted");
                loadData();
            } else {
                toast.error(result.message || "Failed to delete template");
            }
        } catch (e) {
            toast.error("Failed to delete template");
        }
    };

    const handleDeleteSelected = async () => {
        if (!selectedIds.length) {
            toast.error("Select at least one template");
            return;
        }
        const confirmed = await Swal.fire({
            title: "Delete selected templates?",
            text: `Delete ${selectedIds.length} template(s)? This cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
        });
        if (!confirmed.isConfirmed) return;
        setBulkDeleting(true);
        try {
            const res = await fetchWithAuth(Utils.baseurl + "application/push/templates/delete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ app_id: appId, ids: selectedIds }),
            });
            const result = await res.json();
            if (result.status) {
                toast.success(result.message || "Templates deleted");
                loadData();
            } else {
                toast.error(result.message || "Failed to delete templates");
            }
        } catch (e) {
            toast.error("Failed to delete templates");
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv("application/push/templates/list", {
                app_id: appId,
                search: (filters.search || "").trim() || undefined,
            }, "notification-templates.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(String(r.id)));

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>Templates</h1>
                    </div>
                    <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        {can("push.manage") ? (
                            <button
                                type="button"
                                className="add_app_btn_secondary btn_theme_secondary"
                                onClick={handleDeleteSelected}
                                disabled={loading || bulkDeleting || selectedIds.length === 0}
                                title="Delete selected templates"
                            >
                                <MdDeleteOutline />
                                <span>
                                    {bulkDeleting
                                        ? "Deleting…"
                                        : selectedIds.length > 0
                                            ? `Delete (${selectedIds.length})`
                                            : "Delete selected"}
                                </span>
                            </button>
                        ) : null}
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()} disabled={loading || bulkDeleting}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={loading || bulkDeleting || isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? "Exporting…" : "Export"}</span>
                        </button>
                        {can("push.manage") ? (
                            <button
                                type="button"
                                className="add_app_btn btn_theme_primary"
                                onClick={() => navigate(`/apps/${appId}/push-notification?mode=template`)}
                            >
                                <MdAddCircle />
                                <span>Add Template</span>
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="notification-templates-filter-panel"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
                            size={22}
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div className="list-page-filter-accordion-body" id="notification-templates-filter-panel">
                            <div className="list-page-filter-bar">
                                <div className="list-page-filter-fields row g-3 align-items-end">
                                    <div className="list-page-filter-field col-12 col-lg-4">
                                        <input
                                            className="form-control"
                                            placeholder="Search by name"
                                            value={draftFilters.search}
                                            onChange={(e) => setDraftFilters((p) => ({ ...p, search: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === "Enter") onApplyFilters(); }}
                                        />
                                    </div>
                                    <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-auto ms-lg-auto">
                                        <div className="add_app" style={{ justifyContent: "flex-end", width: "100%" }}>
                                            <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}>
                                                <MdSearch />
                                                <span>Search</span>
                                            </button>
                                            <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}>
                                                <MdRefresh />
                                                <span>Reset</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Collapse>
                </div>

                <div className="dashboard_app_table dashboard_app_table--list-records">
                    <div className="dashboard_table">
                        <div ref={tableScrollRef} className="table_scroll_wrapper">
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                                    overflowY: "auto",
                                    overflowX: "auto",
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }} className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={allSelected}
                                                    onChange={toggleSelectAll}
                                                    disabled={loading || bulkDeleting || rows.length === 0}
                                                    title="Select all"
                                                    aria-label="Select all templates"
                                                />
                                            </th>
                                            <th>Name</th>
                                            <th>Title</th>
                                            <th>Preview</th>
                                            <th>Updated</th>
                                            <th className="col-action" style={{ textAlign: "center" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && !rows.length ? (
                                            <tr><td colSpan={6} className="text-center text-muted">Loading…</td></tr>
                                        ) : rows.length === 0 ? (
                                            <tr><td colSpan={6} className="text-center text-muted">No templates found</td></tr>
                                        ) : rows.map((r) => (
                                            <tr key={r.id}>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedIds.includes(String(r.id))}
                                                        onChange={() => toggleSelected(r.id)}
                                                        disabled={loading || bulkDeleting}
                                                        aria-label={`Select ${r.name || "template"}`}
                                                    />
                                                </td>
                                                <td><span className="fw-medium">{r.name}</span></td>
                                                <td>{r.title || "-"}</td>
                                                <td className="small text-muted" style={{ maxWidth: 260, whiteSpace: "normal" }} title={r.message || ""}>
                                                    {r.message || "-"}
                                                </td>
                                                <td>{r.updatedAt ? formatDateTime(r.updatedAt) : "-"}</td>
                                                <td className="col-action" style={{ textAlign: "center" }}>
                                                    <button
                                                        type="button"
                                                        className="dropdown-trigger-btn btn btn-link p-0"
                                                        aria-label="Actions"
                                                        aria-expanded={openActionId === r.id}
                                                        onClick={(e) => handleOpenDropdown(e, r.id, r)}
                                                    >
                                                        <BsThreeDotsVertical />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={page}
                                setPage={(n) => writeParams(n, filters)}
                                total={meta.total || 0}
                                pageSize={pageSize}
                                totalPages={meta.totalPages || 1}
                                ariaLabel="Push templates pagination"
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {openActionItem && dropdownPosition
                ? createPortal(
                    <div
                        ref={dropdownRef}
                        style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: "170px" }}
                    >
                        <div className="dropdown-menu-subscription">
                            {can("push.manage") ? (
                                <button
                                    type="button"
                                    onClick={() => handleDropdownAction(() => navigate(`/apps/${appId}/push-notification?mode=template&template_id=${openActionItem.id}`))}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                                >
                                    <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                                    <span style={{ fontSize: "0.875rem" }}>Edit</span>
                                </button>
                            ) : null}
                            {can("push.manage") ? (
                                <button
                                    type="button"
                                    className="delete-class"
                                    onClick={() => handleDropdownAction(() => handleDelete(openActionItem))}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                                >
                                    <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                                    <span style={{ fontSize: "0.875rem" }}>Delete</span>
                                </button>
                            ) : null}
                        </div>
                    </div>,
                    document.body
                )
                : null}
        </div>
    );
};

export default PushTemplatesList;
